import { fetchApi, type GenericRes } from '@hunk/shared/api/fetchApi';
import type { DeliveryMethodType, OrderStatusType, PaymentOptionType, TransactionStatusType } from '@hunk/shared/common';

/** The logged-in customer's orders: checkout, list, one order, pay again. Under /api/orders (login). Money in agorot. */
const O = '/api/orders';

export interface CartLine {
  productId: number;
  qty: number;
}

export interface CheckoutAddress {
  fullName: string;
  phone: string;
  email?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
  apartment?: string;
  zip?: string;
  notes?: string;
}

export interface CheckoutReq extends CheckoutAddress {
  items: CartLine[];
  /** DeliveryOption.id */
  deliveryOptionId: string;
  discreet: boolean;
  paymentOption: PaymentOptionType;
  /** where the user is: decides where the provider returns to */
  app: 'web' | 'mini';
}

export interface StartPaymentRes extends GenericRes {
  orderId?: number;
  internalTransactionId?: string;
  /** Tranzila / PayPal page to open */
  url?: string;
}

export interface OrderItemView {
  productId: number | null;
  name: string;
  sku: string | null;
  unitPrice: number;
  qty: number;
  image: string | null;
}

export interface OrderView {
  id: number;
  /** "#1042" */
  number: string;
  status: OrderStatusType;
  createdOn: string;
  paidOn: string | null;
  deliveryMethod: DeliveryMethodType;
  /** the chosen option as it was at checkout: "name - description" (null on old courier orders) */
  deliveryLabel: string | null;
  discreet: boolean;
  fullName: string;
  phone: string;
  email: string | null;
  city: string | null;
  street: string | null;
  houseNumber: string | null;
  apartment: string | null;
  zip: string | null;
  notes: string | null;
  itemsTotal: number;
  shippingFee: number;
  total: number;
  trackingNumber: string | null;
  items: OrderItemView[];
}

export interface MyOrdersRes extends GenericRes {
  orders?: OrderView[];
}
export interface MyOrderRes extends GenericRes {
  order?: OrderView;
  /** last payment attempt (to resume polling after the provider's return) */
  payment?: { internalTransactionId: string; status: TransactionStatusType };
}

export interface PaymentStatusRes extends GenericRes {
  status?: TransactionStatusType;
  orderId?: number;
  orderStatus?: OrderStatusType;
}

/** last details the user ordered with (pre-fills the checkout) */
export interface LastDetailsRes extends GenericRes {
  details?: CheckoutAddress;
}

type H = { headers?: HeadersInit };
export const checkout = (data: CheckoutReq, { headers }: H) => fetchApi<CheckoutReq, StartPaymentRes>({ apiPath: `${O}/checkout`, method: 'POST', data, headers });
export const myOrders = ({ headers }: H) => fetchApi<{}, MyOrdersRes>({ apiPath: O, headers });
export const myOrder = (id: number, { headers }: H) => fetchApi<{}, MyOrderRes>({ apiPath: `${O}/${id}`, headers });
export const lastDetails = ({ headers }: H) => fetchApi<{}, LastDetailsRes>({ apiPath: `${O}/lastDetails`, headers });
/** an unpaid order: start a new payment */
export const payOrder = (id: number, data: { paymentOption: PaymentOptionType; app: 'web' | 'mini' }, { headers }: H) =>
  fetchApi<typeof data, StartPaymentRes>({ apiPath: `${O}/${id}/pay`, method: 'POST', data, headers });
export const paymentStatus = (internalTransactionId: string, { headers }: H) =>
  fetchApi<{ internalTransactionId: string }, PaymentStatusRes>({ apiPath: '/api/billing/status', data: { internalTransactionId }, headers });
